import React, { useState, useEffect } from "react";
import ModalBase from "./ModalBase";
import { useFeedback } from "@contexts/FeedbackContext";
import { HiClipboardList, HiPaperAirplane } from "react-icons/hi";

interface ModalEnvioIndividualProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (email: string, telefone: string) => Promise<void>;
  itemNome: string;
  fornecedorNome: string;
  emailPadrao?: string;
  telefonePadrao?: string;
}

const ModalEnvioIndividual: React.FC<ModalEnvioIndividualProps> = ({
  isOpen,
  onClose,
  onSend,
  itemNome,
  fornecedorNome,
  emailPadrao = "",
  telefonePadrao = "",
}) => {
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const { showError } = useFeedback();

  // Atualizar sempre que a modal abrir OU quando as props mudarem
  useEffect(() => {
    if (isOpen) {
      setEmail(emailPadrao || "");
      setTelefone(telefonePadrao || "");
      setEmailError("");
    }
  }, [isOpen, emailPadrao, telefonePadrao]);

  // Limpar estado ao fechar
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setTelefone("");
      setEmailError("");
      setSending(false);
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    // Remove espaços em branco e valida formato de e-mail
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return false;

    // Rejeitar e-mails com vírgulas ou outros caracteres inválidos
    if (/[,;\s<>\[\]\(\)\\"']/.test(trimmedEmail)) {
      return false;
    }

    // Regex robusta para validação de e-mail
    // Formato: usuario@dominio.extensao
    const emailRegex =
      /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
    return emailRegex.test(trimmedEmail);
  };

  const handleSend = async () => {
    // Validação - trim para remover espaços
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setEmailError("O campo de e-mail é obrigatório");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setEmailError(
        "Por favor, insira um e-mail válido (exemplo: usuario@dominio.com)",
      );
      return;
    }

    setEmailError("");
    setSending(true);

    try {
      // Enviar com email trimmed
      await onSend(trimmedEmail, telefone);
      onClose();
    } catch (error) {
      console.error("Erro ao enviar solicitação:", error);
      showError("Erro ao enviar solicitação de orçamento. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <div className="pb-6 border-b border-base-300 mb-6">
        <h2 className="text-xl font-bold mb-2 text-base-content flex items-center gap-2">
          <HiClipboardList className="h-5 w-5" />
          Solicitação de orçamento individual
        </h2>
        <p className="text-sm text-base-content/70">
          <strong>Item da cotação:</strong> {itemNome}
        </p>
        <p className="text-sm text-base-content/70">
          <strong>Fornecedor destinatário:</strong> {fornecedorNome}
        </p>
      </div>

      <div className="space-y-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">
              Informe o E-mail que deverá receber a solicitação de orçamento *
            </span>
          </label>
          <input
            id="email"
            type="email"
            className={`input input-bordered ${
              emailError ? "input-error" : ""
            }`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError("");
            }}
            placeholder={
              emailPadrao
                ? "E-mail pré-preenchido do cadastro"
                : "exemplo@email.com"
            }
            disabled={sending}
          />
          {emailError && (
            <label className="label">
              <span className="label-text-alt text-error">{emailError}</span>
            </label>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-base-300 pt-4 mt-6">
        <button className="btn btn-ghost" onClick={onClose} disabled={sending}>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={sending || !email.trim() || !!emailError}
        >
          {sending ? (
            <>
              <span className="loading loading-spinner"></span>
              Enviando...
            </>
          ) : (
            <>
              <HiPaperAirplane className="h-4 w-4" />
              Enviar
            </>
          )}
        </button>
      </div>
    </ModalBase>
  );
};

export default ModalEnvioIndividual;
